<template>
  <loader v-if="loading"/>
  <full-screen-modal v-else :close-action="closeAction" :back-action="backAction" class="content-wrapper">
    <template v-slot:content>
      <credentials-with-async-fallback
        :is-loading="isLoading"
        :async-timeout-reached="asyncTimeoutReached"
        :save-credentials-error="saveCredentialsError"
        :source-name="sourceName"
        :on-timeout="onTimeout"
        :loading-start-time="loadingStartTime"
        class="content-wrapper">
        <template v-slot:credentials v-if="source">
          <discount-credentials v-if="isDiscountSource"
                                :invalid-credentials="invalidCredentials"
                                :source="source"
                                :name-already-used-error="nameAlreadyUsedError"
                                :set-name-already-in-use-error="setNameAlreadyInUseError"
                                :save-credentials-error="saveCredentialsError"
                                :validate-name="validateName"
                                :save-discount-direct="saveDiscountDirect"
                                :update-mode="updateMode"
                                :on-save="onSave"/>
          <source-connection-details v-else
                             :invalid-credentials="invalidCredentials"
                             :source="source"
                             :name-already-used-error="nameAlreadyUsedError"
                             :set-name-already-in-use-error="setNameAlreadyInUseError"
                             :save-credentials-error="saveCredentialsError"
                             :validate-name="validateName"
                             :update-mode="updateMode"
                             :on-save="onSave"
                             :prevent-o-bk-connection="preventOBkConnection"/>
        </template>
      </credentials-with-async-fallback>
    </template>
  </full-screen-modal>
</template>

<script>
import _ from 'lodash';
import CredentialsWithAsyncFallback
  from '@/pages/responsive-pages/authenticated/onboard2/CredentialsWithAsyncFallback';
import { mapState } from 'vuex';
import FullScreenModal from '@/base-components/FullScreenModal';
import DiscountCredentials from '@/base-components/onboarding/discount/DiscountCredentials';
import Loader from '@/base-components/Loader';
import SourceConnectionDetails from '@/base-components/onboarding/openBanking/SourceConnectionDetails';
import { getSourceConfigurationBySourceName } from '@/constants/sourcesConfiguration';
import Segment from '@/Segment';
import promotionConsts from '@/constants/promotions';
import OpenBankingMixin from '@/base-components/onboarding/openBanking/OpenBankingMixin';

export default {
  name: 'ConnectionFlowModal',
  mixins: [OpenBankingMixin],
  components: {
    SourceConnectionDetails,
    FullScreenModal,
    CredentialsWithAsyncFallback,
    DiscountCredentials,
    Loader,
  },
  data() {
    return {
      loading: false,
      shouldIdentifySegmentForDiscountPromotion: false,
      discountObkSupported: true,
    };
  },
  async created() {
    this.loading = true;
    this.loading = false;
    // settings this prop here and not as a computed prop because while submitting the credentials the sourceName prop changes to null
    // makes "isDiscountSource" false while handling "onConnectionSuccess"
    this.shouldIdentifySegmentForDiscountPromotion = this.isDiscountSource && !this.benefitId;
    this.discountObkSupported = await this.isOpenBankingSupported({ sourceId: 'discount' });
  },
  props: {
    closeAction: {
      type: Function,
      required: true,
    },
    backAction: {
      type: Function,
      required: false,
    },
    sourceType: {
      type: String,
      required: true,
    },
    invalidCredentials: {
      type: Boolean,
      required: true,
    },
    saveCredentialsError: {
      type: Boolean,
      required: true,
    },
    nameAlreadyUsedError: {
      type: Boolean,
      required: true,
    },
    onSave: {
      type: Function,
      required: true,
    },
    setNameAlreadyInUseError: {
      type: Function,
      required: true,
    },
    sourceName: {
      type: String,
    },
    validateName: {
      type: Function,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    updateMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    next: {
      type: Function,
      required: true,
    },
    onConnectionSuccess: {
      type: Function,
      required: true,
    },
    saveDiscountDirect: {
      type: Function,
      required: true,
    },
    asyncTimeoutReached: {
      type: Boolean,
      required: true,
    },
    onTimeout: {
      type: Function,
      required: true,
    },
    loadingStartTime: {
      type: Date,
    },
    preventOBkConnection: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('benefits', ['benefitId']),
    ...mapState('discountPromotion', ['eligibleForDiscountPromotion']),
    source() {
      if (this.sourceNameIsDiscount(this.sourceName) && this.discountObkSupported) {
        return getSourceConfigurationBySourceName('discount');
      }
      return getSourceConfigurationBySourceName(this.sourceName);
    },
    isDiscountSource() {
      return this.sourceNameIsDiscount(this.sourceName) && !this.discountObkSupported;
    },
  },
  methods: {
    sourceNameIsDiscount(sourceName) {
      return _.includes(['דיסקונט ישיר', 'דיסקונט בסיס', 'דיסקונט'], sourceName);
    },
  },
  watch: {
    async isLoading(newValue, oldValue) {
      if (oldValue && !newValue && !this.saveCredentialsError && !this.invalidCredentials) {
        if (this.shouldIdentifySegmentForDiscountPromotion) {
          Segment.identifyCustomer(this.customerId, {
            promotion: promotionConsts.PROMOTION_NAMES.DISCOUNT_TWO_MONTHS_FREE_BY_CREDS,
          });
        }
        await this.onConnectionSuccess();
        this.next();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.content-wrapper {
  text-align: right;
}
</style>
